import { useEffect, useRef, useState } from "react";
import { Check, Download, Shield, Zap, Loader2 } from "lucide-react";

const ProgressStepper = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const onCompleteRef = useRef(onComplete);
    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

    const steps = [
        { id: 1, title: "Verifying your connection", description: "Checking your device and network...", icon: Shield, duration: 4000 },
        { id: 2, title: "Preparing Download", description: "Securing your file and setting up access...", icon: Zap, duration: 4000 },
    ];

    useEffect(() => {
        if (currentStep >= steps.length) { setTimeout(() => onCompleteRef.current(), 500); return; }
        const step = steps[currentStep];
        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / step.duration) * 100, 100);
            setProgress(newProgress);
            if (newProgress >= 100) { clearInterval(interval); setTimeout(() => { setCurrentStep(currentStep + 1); setProgress(0); }, 300); }
        }, 50);
        return () => clearInterval(interval);
    }, [currentStep]);

    return (
        <div className="mt-[16px]">
            <style>{`
                @keyframes stepperFadeIn {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes iconGlow {
                    0%,100% { box-shadow: 0 0 12px rgba(70,143,175,0.4), 0 0 0 1px rgba(70,143,175,0.3); }
                    50%     { box-shadow: 0 0 24px rgba(70,143,175,0.7), 0 0 0 1px rgba(70,143,175,0.6); }
                }
                @keyframes progressShimmer {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes completePop {
                    0%   { transform: scale(0.6); opacity: 0; }
                    60%  { transform: scale(1.15); }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>

            {/* Subtle ambient glow behind card */}
            <div
                className="absolute inset-0 pointer-events-none -z-10"
                style={{
                    background: "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(70,143,175,0.08) 0%, transparent 70%)",
                }}
            />

            <div className="max-w-2xl w-full" style={{ animation: "stepperFadeIn 0.45s ease forwards" }}>
                <div
                    className="rounded-3xl p-6 relative overflow-hidden"
                    style={{
                        background: "linear-gradient(180deg, #010f1e 0%, #012a4a 100%)",
                        border: "1px solid rgba(70,143,175,0.18)",
                        boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.03) inset",
                    }}
                >
                    {/* Top edge highlight */}
                    <div
                        className="absolute top-0 left-8 right-8 h-px"
                        style={{ background: "linear-gradient(90deg, transparent, rgba(70,143,175,0.5), transparent)" }}
                    />

                    {/* Subtle grid texture overlay */}
                    <div
                        className="absolute inset-0 pointer-events-none opacity-[0.025]"
                        style={{
                            backgroundImage: "linear-gradient(rgba(70,143,175,1) 1px, transparent 1px), linear-gradient(90deg, rgba(70,143,175,1) 1px, transparent 1px)",
                            backgroundSize: "32px 32px",
                        }}
                    />

                    {/* Header */}
                    <div className="text-center mb-7 relative">
                        {/* Icon pill */}
                        <div className="flex justify-center mb-4">
                            <div
                                className="flex items-center justify-center w-14 h-14 rounded-2xl"
                                style={{
                                    background: "rgba(70,143,175,0.12)",
                                    border: "1px solid rgba(70,143,175,0.3)",
                                    boxShadow: "0 0 24px rgba(70,143,175,0.2), 0 4px 16px rgba(0,0,0,0.4)",
                                }}
                            >
                                <Download className="w-6 h-6" style={{ color: "#89d4f5" }} />
                            </div>
                        </div>
                        <h2 className="semiBold text-white mb-1" style={{ fontSize: "22px", letterSpacing: "0.01em" }}>
                            Preparing Your Download
                        </h2>
                        <p className="medium" style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)" }}>
                            Please wait while we set everything up…
                        </p>
                    </div>

                    {/* Steps */}
                    <div className="flex flex-col gap-4 mb-6 relative">
                        {/* Connector line between steps */}
                        <div
                            className="absolute left-6 top-12 bottom-12 w-px"
                            style={{ background: "linear-gradient(180deg, rgba(70,143,175,0.3), rgba(70,143,175,0.08))" }}
                        />

                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = index === currentStep;
                            const isCompleted = index < currentStep;

                            return (
                                <div
                                    key={step.id}
                                    className="flex items-start gap-4 relative transition-all duration-500"
                                    style={{ opacity: (!isActive && !isCompleted) ? 0.45 : 1 }}
                                >
                                    {/* Step icon */}
                                    <div className="flex-shrink-0 z-10">
                                        <div
                                            className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500"
                                            style={
                                                isCompleted ? {
                                                    background: "linear-gradient(135deg, #15803d, #16a34a)",
                                                    boxShadow: "0 4px 16px rgba(22,163,74,0.4)",
                                                    animation: "completePop 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards",
                                                } : isActive ? {
                                                    background: "rgba(70,143,175,0.18)",
                                                    border: "1px solid rgba(70,143,175,0.45)",
                                                    animation: "iconGlow 2s ease-in-out infinite",
                                                } : {
                                                    background: "rgba(255,255,255,0.05)",
                                                    border: "1px solid rgba(255,255,255,0.07)",
                                                }
                                            }
                                        >
                                            {isCompleted ? (
                                                <Check className="w-5 h-5 text-white" />
                                            ) : isActive ? (
                                                <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#89d4f5" }} />
                                            ) : (
                                                <Icon className="w-5 h-5" style={{ color: "rgba(255,255,255,0.25)" }} />
                                            )}
                                        </div>
                                    </div>

                                    {/* Step content */}
                                    <div className="flex-1 min-w-0 pt-1.5">
                                        <div
                                            className="semiBold transition-all duration-500 leading-tight"
                                            style={{
                                                fontSize: "16px",
                                                color: (isActive || isCompleted) ? "#fff" : "rgba(255,255,255,0.3)",
                                            }}
                                        >
                                            {step.title}
                                        </div>
                                        <div
                                            className="medium mt-0.5 transition-all duration-500"
                                            style={{
                                                fontSize: "13px",
                                                color: isActive ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.2)",
                                            }}
                                        >
                                            {step.description}
                                        </div>

                                        {/* Progress bar */}
                                        {isActive && (
                                            <div
                                                className="mt-3 rounded-full overflow-hidden"
                                                style={{ height: "4px", background: "rgba(255,255,255,0.08)" }}
                                            >
                                                <div
                                                    className="h-full rounded-full transition-all duration-300"
                                                    style={{
                                                        width: `${progress}%`,
                                                        background: "linear-gradient(90deg, #01497c, #468faf, #89d4f5)",
                                                        backgroundSize: "200% 100%",
                                                        animation: "progressShimmer 2s linear infinite",
                                                        boxShadow: "0 0 8px rgba(70,143,175,0.6)",
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Completed bar (full, green) */}
                                        {isCompleted && (
                                            <div
                                                className="mt-3 rounded-full overflow-hidden"
                                                style={{ height: "4px", background: "rgba(255,255,255,0.08)" }}
                                            >
                                                <div
                                                    className="h-full rounded-full w-full"
                                                    style={{ background: "linear-gradient(90deg, #15803d, #22c55e)" }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Overall progress */}
                    <div
                        className="rounded-2xl px-4 py-3"
                        style={{
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.07)",
                        }}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="medium" style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)" }}>
                                Overall Progress
                            </span>
                            <span className="semiBold text-white" style={{ fontSize: "13px" }}>
                                {currentStep} / {steps.length} steps
                            </span>
                        </div>
                        <div
                            className="rounded-full overflow-hidden"
                            style={{ height: "4px", background: "rgba(255,255,255,0.08)" }}
                        >
                            <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                    width: `${(currentStep / steps.length) * 100}%`,
                                    background: "linear-gradient(90deg, #01497c, #468faf)",
                                    boxShadow: "0 0 8px rgba(70,143,175,0.5)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgressStepper;
